/* ==========================================================================
   TANJORE SMART CART — runtime
   Added 2026-09-24.

   The drawer itself is rendered entirely in Liquid: Prestige re-renders the
   whole `cart-drawer` section over the Section Rendering API after every cart
   change, so the progress bar, savings figures and totals refresh on their
   own. This file only handles the two things Liquid cannot do:

     1. FREE GIFT  — add / remove the gift variant when the cart crosses the
                     threshold.
     2. SHIPROCKET — re-mount the headless checkout button after the section
                     has been replaced in the DOM.

   Config comes from data attributes on `[data-tsc]` inside the drawer, so
   everything stays editable from the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  /* The cart drawer section re-renders on every cart change; if its script tag
     is ever re-executed, bail out so listeners are only registered once. */
  if (window.__tanjoreSmartCartLoaded) return;
  window.__tanjoreSmartCartLoaded = true;

  var GIFT_PROPERTY = '_free_gift';
  var inFlight = false;
  var lastSyncSignature = null;

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function config() {
    var el = document.querySelector('[data-tsc]');
    if (!el) return null;

    return {
      giftEnabled: el.getAttribute('data-tsc-gift') === 'true',
      giftVariantId: el.getAttribute('data-tsc-gift-variant') || '',
      giftThreshold: parseInt(el.getAttribute('data-tsc-gift-threshold') || '0', 10)
    };
  }

  function fetchCart() {
    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      return response.json();
    });
  }

  /* The value the thresholds are measured against: every line except the gift
     itself, minus any cart-level discount. Mirrors the Liquid in
     `snippets/cart-milestones.liquid` so the bar and the gift never disagree. */
  function qualifyingTotal(cart) {
    var total = 0;

    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties[GIFT_PROPERTY]) return;
      total += item.final_line_price;
    });

    (cart.cart_level_discount_applications || []).forEach(function (discount) {
      total -= discount.total_allocated_amount;
    });

    return Math.max(total, 0);
  }

  function findGiftLine(cart) {
    var match = null;

    (cart.items || []).forEach(function (item, index) {
      if (item.properties && item.properties[GIFT_PROPERTY]) {
        match = { item: item, line: index + 1 };
      }
    });

    return match;
  }

  function announceCartChange() {
    /* Prestige listens for `cart:refresh` and re-renders every bundled cart
       section, which is exactly what we want after adding or removing the
       gift. Dispatched on both targets because the listener has moved between
       theme versions. */
    var event = function () {
      return new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'tanjore-smart-cart' } });
    };

    document.dispatchEvent(event());
    window.dispatchEvent(event());
  }

  function addGift(settings) {
    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: Number(settings.giftVariantId),
            quantity: 1,
            properties: (function () {
              var properties = {};
              properties[GIFT_PROPERTY] = 'true';
              return properties;
            })()
          }
        ]
      })
    });
  }

  function removeGift(giftLine) {
    return fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: giftLine.line, quantity: 0 })
    });
  }

  function syncGift() {
    var settings = config();

    if (!settings || !settings.giftEnabled || !settings.giftVariantId || !settings.giftThreshold) {
      return Promise.resolve();
    }

    if (inFlight) return Promise.resolve();

    return fetchCart()
      .then(function (cart) {
        var total = qualifyingTotal(cart);
        var gift = findGiftLine(cart);
        var qualifies = total >= settings.giftThreshold;

        /* Guard against the add -> cart:refresh -> cart:change -> add loop. */
        var signature = [total, qualifies, gift ? gift.item.variant_id : 0].join(':');
        if (signature === lastSyncSignature) return null;
        lastSyncSignature = signature;

        if (qualifies && !gift) {
          inFlight = true;
          return addGift(settings);
        }

        if (!qualifies && gift) {
          inFlight = true;
          return removeGift(gift);
        }

        /* The merchant changed the gift product in the editor — swap it out. */
        if (qualifies && gift && String(gift.item.variant_id) !== String(settings.giftVariantId)) {
          inFlight = true;
          return removeGift(gift).then(function () {
            return addGift(settings);
          });
        }

        return null;
      })
      .then(function (changed) {
        if (!changed) return;
        inFlight = false;
        lastSyncSignature = null;
        announceCartChange();
      })
      .catch(function (error) {
        inFlight = false;
        if (window.console) console.warn('[Tanjore Smart Cart] gift sync failed', error);
      });
  }

  /* ------------------------------------------------------------------------
     Shiprocket headless button
     ------------------------------------------------------------------------
     Shiprocket's shopify.js mounts its button into `.shiprocket-headless` on
     page load. When Prestige replaces the cart drawer section the holder is a
     brand new, empty node, so we re-insert it to re-trigger their observer.
     If they ever expose an official re-init hook, call it here instead.
  */
  function remountShiprocket(attempt) {
    attempt = attempt || 0;

    var holder = document.querySelector('.cart-drawer .shiprocket-headless');
    if (!holder) return;

    if (holder.children.length > 0) return;

    var replacement = holder.cloneNode(false);
    holder.parentNode.replaceChild(replacement, holder);

    if (attempt < 6) {
      setTimeout(function () {
        remountShiprocket(attempt + 1);
      }, 600);
    }
  }

  function onCartChanged() {
    syncGift();
    setTimeout(function () {
      remountShiprocket(0);
    }, 250);
  }

  document.addEventListener('cart:change', onCartChanged);
  document.addEventListener('cart:refresh', function (event) {
    if (event.detail && event.detail.source === 'tanjore-smart-cart') return;
    setTimeout(function () {
      remountShiprocket(0);
    }, 400);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGift);
  } else {
    syncGift();
  }
})();
